<template>
  <section class="feedback-admin-container tw-page">
    <PageHeader title="Admin Feedback" />
    <b-tabs v-model="activeTab" class="feedback-tabs">
      <b-tab-item label="Unread">
        <b-table
          :data="unreadFeedback"
          :columns="unreadTableColumns"
          :striped="true"
          :hoverable="true"
          :paginated="true"
          :per-page="10"
          :default-sort="['createdAt', 'desc']"
          class="feedback-table"
          @click="openModal"
        >
          <template #empty>
            <div class="empty-msg">No unread feedback found.</div>
          </template>
        </b-table>
      </b-tab-item>
      <b-tab-item label="Read">
        <b-table
          :data="readFeedback"
          :columns="readTableColumns"
          :striped="true"
          :hoverable="true"
          :paginated="true"
          :per-page="10"
          :default-sort="['createdAt', 'desc']"
          class="feedback-table"
          @row-click="openModal"
        >
          <template #empty>
            <div class="empty-msg">No read feedback found.</div>
          </template>
        </b-table>
      </b-tab-item>
    </b-tabs>

    <!-- Feedback Modal -->
    <b-modal
      v-model="showModal"
      :can-cancel="['escape', 'outside', 'button']"
      :width="480"
      trap-focus
    >
      <div class="modal-content" v-if="selectedFeedback">
        <h2 class="modal-title">
          <i class="mdi mdi-message-text-outline"></i>
          Feedback Details
        </h2>
        <div class="modal-details">
          <div><strong>First Name:</strong> {{ selectedFeedback.user?.firstName || '-' }}</div>
          <div><strong>Last Name:</strong> {{ selectedFeedback.user?.lastName || '-' }}</div>
          <div><strong>Email Address:</strong> {{ selectedFeedback.user?.email || '-' }}</div>
          <div><strong>Area:</strong> {{ selectedFeedback.area || '-' }}</div>
          <div><strong>Date Submitted:</strong> {{ formatDate(selectedFeedback.createdAt) }}</div>
        </div>
        <div class="modal-message">
          <strong>Message:</strong>
          <div class="message-box">{{ selectedFeedback.message || '-' }}</div>
        </div>
        <div class="modal-actions">
          <b-button type="is-text" @click="closeModal">Close</b-button>
          <b-button
            v-if="!selectedFeedback.isRead"
            type="is-success"
            @click="markAsRead(selectedFeedback)"
            :loading="markingRead"
          >
            Mark as Read
          </b-button>
        </div>
      </div>
    </b-modal>
    <p v-if="error" class="error-msg">{{ error }}</p>
  </section>
</template>

<script>
import PageHeader from '@/components/layout/PageHeader.vue'
import moment from 'moment';
import { useUserStore } from '@/stores/userStore';
import { getUser } from '../../utils/helpers.js';

export default {
  name: 'AdminFeedback',
  components: {
    PageHeader,
  },
  data() {
    return {
      user: null,
      feedback: [],
      error: '',
      activeTab: 0,
      showModal: false,
      selectedFeedback: null,
      markingRead: false,
      config: {
        headers: { 'Authorization': this.$cookies.get('teamwrkr-token') }
      },
      unreadTableColumns: [
        { field: 'user.firstName', label: 'First Name', sortable: true },
        { field: 'user.lastName', label: 'Last Name', sortable: true },
        { field: 'user.email', label: 'Email Address', sortable: true },
        { field: 'area', label: 'Area', sortable: true },
        { field: 'createdAt', label: 'Date Submitted', sortable: true }
      ],
      readTableColumns: [
        { field: 'user.firstName', label: 'First Name', sortable: true },
        { field: 'user.lastName', label: 'Last Name', sortable: true },
        { field: 'user.email', label: 'Email Address', sortable: true },
        { field: 'area', label: 'Area', sortable: true },
        { field: 'updatedAt', label: 'Date Marked Read', sortable: true }
      ]
    };
  },
  computed: {
    unreadFeedback() {
      return this.feedback.filter(f => !f.read);
    },
    readFeedback() {
      return this.feedback.filter(f => f.read);
    }
  },
  created() {
    this.userStore = useUserStore()
    this.user = this.userStore.user;
    if (this.user && this.user._id) {
      this.getFeedback();
    } else {
      this.fetchUserData();
    }
  },
  methods: {
    async getFeedback() {
      try {
        const response = await this.axios.get(
          `${process.env.VUE_APP_API_URL}/feedback`,
          this.config
        );
        this.feedback = response.data;
      } catch (err) {
        this.error = err?.response?.data?.message || 'Could not get feedback. Please try again.';
      }
    },
    async fetchUserData() {
      try {
        const token = this.$cookies.get('teamwrkr-token');
        const userData = await getUser(token);
        this.userStore.setUser(userData);
        this.user = userData;
        this.getFeedback();
      } catch (err) {
        this.error = 'Error fetching user data';
      }
    },
    formatDate(dateStr) {
      return moment(dateStr).format('MMMM Do YYYY, h:mm:ss a');
    },
    openModal(row) {
      this.selectedFeedback = row;
      this.showModal = true;
      this.markingRead = false;
    },
    closeModal() {
      this.showModal = false;
      this.selectedFeedback = null;
      this.markingRead = false;
    },
    async markAsRead(row) {
      console.log("Marking as read:", row);
      this.markingRead = true;
      try {
        await this.axios.post(
          `${process.env.VUE_APP_API_URL}/feedback/${row._id}/mark-as-read`,
          {},
          this.config
        );
        this.$buefy.toast.open({
          message: 'Marked as read!',
          type: 'is-success'
        });
        this.showModal = false;
        this.getFeedback();
      } catch (err) {
        this.$buefy.toast.open({
          message: 'Could not mark as read.',
          type: 'is-danger'
        });
      } finally {
        this.markingRead = false;
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.feedback-admin-container {
  max-width: 900px;
  margin: $space-7 auto;
  padding: $space-6 $space-4;
  background: $color-surface-alt;
  border-radius: $radius-xl;
  box-shadow: 0 4px 32px rgba(170, 118, 214, 0.07);
  color: $color-text;
}

.feedback-tabs {
  margin-bottom: $space-6;
}

.feedback-table {
  background: $color-surface;
  border-radius: $radius-lg;
  box-shadow: 0 2px 12px rgba(170, 118, 214, 0.05);
  padding: $space-4;
}

.empty-msg {
  text-align: center;
  color: $color-accent;
  font-weight: 600;
  padding: $space-6 0;
}

.error-msg {
  color: $color-accent;
  font-weight: 600;
  margin-top: $space-4;
  text-align: center;
}

.modal-content {
  padding: $space-5 $space-4;
  background: $color-surface;
  border-radius: $radius-lg;
  color: $color-text;
  display: flex;
  flex-direction: column;
  gap: $space-4;
}

.modal-title {
  font-size: $font-size-lg;
  font-weight: 700;
  color: $color-primary;
  display: flex;
  align-items: center;
  gap: $space-2;
  margin-bottom: $space-1;
  i {
    color: $color-warning;
    font-size: 1.7rem;
  }
}

.modal-details {
  margin-bottom: $space-4;
  div {
    margin-bottom: $space-1;
    font-size: $font-size-md;
  }
}

.modal-message {
  margin-bottom: $space-4;
  .message-box {
    background: $color-surface-alt;
    border-radius: $radius-sm;
    padding: $space-3;
    margin-top: $space-1;
    color: $color-text;
    font-size: $font-size-md;
    word-break: break-word;
  }
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: $space-4;
  margin-top: $space-2;
}
</style>
